import { useState } from 'react';
import { Text, type LedgerCollapse } from '@billda/design-system';
import { ActivityLedgerDemo } from './ActivityLedgerDemo';
import { PotLedgerDemo } from './PotLedgerDemo';
import { ScreenFrame } from './ScreenFrame';
import { SiteLedgerDemo } from './SiteLedgerDemo';

/**
 * /demo — a harness for the shared ledger layer
 * (`@billda/design-system`), built from the exported components with fake
 * data in the shape of the three screens that render booked work. Each frame
 * owns its own CSS container, so the width toggles exercise the real collapse
 * behaviour. Deleted once the three screens have rolled over.
 */

const SITE_COLLAPSE: LedgerCollapse = { container: 'site-demo', at: 620 };
const POT_COLLAPSE: LedgerCollapse = { container: 'pot-demo', at: 620 };
const ACTIVITY_COLLAPSE: LedgerCollapse = {
  container: 'activity-demo',
  at: 760,
};

export function ListMockupsPage() {
  const [opened, setOpened] = useState<string | null>(null);

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-8">
      <Text as="h2" variant="h5" className="mb-2 font-semibold">
        Booked-work ledger
      </Text>
      <Text
        as="div"
        variant="body2"
        color="secondary"
        className="mb-2 max-w-[72ch]"
      >
        One shared ledger anatomy across the three booked-work screens. Status
        lives in the leading dot, part-booked items render each booking as a
        peer row capped by a subtotal strip, unbooked items read as the plot
        template. Hover a title for the card, right-click a booking for the
        cancel menu, and use the width toggles to preview tablet / phone
        collapse. All data is fake.
      </Text>
      <Text as="div" variant="caption" color="secondary" className="mb-8 block">
        {opened ? `Row activated: ${opened}` : 'Click or press Enter on a row.'}
      </Text>

      <ScreenFrame
        container={SITE_COLLAPSE.container}
        title="Site view · Kiddlington TF01, Plots tab"
        subtitle="Progress across the site: plots → labour items (booked, part-booked, and unbooked template items) → bookings, plus day work"
      >
        <SiteLedgerDemo collapse={SITE_COLLAPSE} onOpen={setOpened} />
      </ScreenFrame>

      <ScreenFrame
        container={POT_COLLAPSE.container}
        title="Pot breakdown · Romas Kvedys, Wk 30"
        subtitle="What the payer reviews: this pot's bookings, day work and deductions, grouped by site · plot"
      >
        <PotLedgerDemo collapse={POT_COLLAPSE} onOpen={setOpened} />
      </ScreenFrame>

      <ScreenFrame
        container={ACTIVITY_COLLAPSE.container}
        title="Activity · all sites, weekly"
        subtitle="Admin review: weeks → every booking org-wide, with site / plot context"
      >
        <ActivityLedgerDemo collapse={ACTIVITY_COLLAPSE} onOpen={setOpened} />
      </ScreenFrame>

      <div className="mt-4 pb-12">
        <Text as="div" variant="caption" color="secondary">
          Shared grammar: 8px status dot (the word reaches screen readers via
          the row label and shows in the collapsed meta line), monospace refs,
          tabular-nums amounts, cancelled rows dimmed and struck, deductions in
          red. Toggle dark/light in Settings to check both modes.
        </Text>
      </div>
    </div>
  );
}
